import { useEffect, useState, useRef } from 'react';

/**
 * Custom hook to check if an element is in the viewport
 * Used for implementing lazy loading of images and other heavy content
 */
export const useIntersectionObserver = (
    options = {
        root: null,
        rootMargin: '200px',
        threshold: 0.1,
    }
) => {
    const [isIntersecting, setIsIntersecting] = useState(false);
    const [hasIntersected, setHasIntersected] = useState(false);
    const elementRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            setIsIntersecting(entry.isIntersecting);

            // Once the element has been intersected, remember it
            if (entry.isIntersecting && !hasIntersected) {
                setHasIntersected(true);
            }
        }, options);

        const currentElement = elementRef.current;
        if (currentElement) {
            observer.observe(currentElement);
        }

        return () => {
            if (currentElement) {
                observer.unobserve(currentElement);
            }
        };
    }, [options, hasIntersected]);

    return { elementRef, isIntersecting, hasIntersected };
};

/**
 * Get the appropriate image source based on the device pixel ratio and screen size
 * This helps to serve the right image size for the user's device
 */
export const getOptimalImageSource = (
    sources: Record<string, string>,
    width: number
): string => {
    const dpr = window.devicePixelRatio || 1;

    // Calculate the effective width needed
    const effectiveWidth = width * dpr;

    // Available sizes (assuming they're named like 'small', 'medium', 'large', etc.)
    const sizeMap = {
        small: 640,
        medium: 1024,
        large: 1920,
        fallback: Infinity,
    };

    // Find the smallest image that still meets our needs
    let bestFit = 'fallback';
    let bestFitDifference = Infinity;

    for (const [size, maxWidth] of Object.entries(sizeMap)) {
        if (maxWidth >= effectiveWidth && maxWidth - effectiveWidth < bestFitDifference) {
            bestFit = size;
            bestFitDifference = maxWidth - effectiveWidth;
        }
    }

    return sources[bestFit] || sources.fallback;
};

/**
 * Generate a background blur placeholder for an image
 * Creates a tiny version of the image that can be shown while the full image loads
 */
export const createBlurPlaceholder = (
    imageUrl: string,
    size = 10
): Promise<string> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';

        img.onload = () => {
            // Create a small canvas
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                reject(new Error('Could not get canvas context'));
                return;
            }

            // Set size
            canvas.width = size;
            canvas.height = size * (img.height / img.width);

            // Draw the image at small size
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            // Get the data URL
            const blurDataUrl = canvas.toDataURL('image/jpeg', 0.1);
            resolve(blurDataUrl);
        };

        img.onerror = () => {
            reject(new Error('Failed to load image for blur placeholder'));
        };

        img.src = imageUrl;
    });
};

/**
 * Calculate the aspectRatio from an image URL
 * This can be used to prevent layout shift before the image loads
 */
export const getImageAspectRatio = (
    imageUrl: string
): Promise<number> => {
    return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
            resolve(img.width / img.height);
        };

        img.onerror = () => {
            reject(new Error('Failed to load image for aspect ratio calculation'));
        };

        img.src = imageUrl;
    });
};

/**
 * Preload critical images to improve perceived performance
 * Use this for important above-the-fold images
 */
export const preloadCriticalImages = (imageUrls: string[]): void => {
    imageUrls.forEach(url => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = url;
        document.head.appendChild(link);
    });
};

export default {
    useIntersectionObserver,
    getOptimalImageSource,
    createBlurPlaceholder,
    getImageAspectRatio,
    preloadCriticalImages,
}; 